import React, {useEffect, useState} from "react";
import { useMapController } from "../hooks/useMapController.jsx";
import MapContent from "./map-content.jsx";
import MapButtonsControl from "../../../../../components/map-report/map-buttons-control.jsx";
import MapPlayerControl from "../../../../../components/map-report/map-player-control.jsx";
import { APIProvider, Map as GoogleMaps } from "@vis.gl/react-google-maps";
import { useQuery } from "@tanstack/react-query";
import CorporationService from "../../../../../services/web/corporation-service.js";
const ReportContent = ({ data, animationMode, isPrinting }) => {
    const controller = useMapController(data);
    const frames = controller.frames;

    const { data: corporation } = useQuery({
        queryKey: ['corporation'],
        queryFn: () => CorporationService.entity(),
    });

    const [mapState] = useState({
        neighborhoodStreets: false,
        pointsOfInterest: false
    });

    const getPosition = (items = []) => {
        const valid = items.filter(i => i.system_latitude && i.system_longitude);

        if (!valid.length) {
            return { lat: -23.0, lng: -46.0 };
        }

        const first = valid[0];

        return {
            lat: Number(first.system_latitude),
            lng: Number(first.system_longitude)
        };
    };

    useEffect(() => {
        if (!data || frames.length < 2 || animationMode === true) return;

        const timeout = setTimeout(() => {
            const start = window.scrollY;
            const end = document.body.scrollHeight;
            const duration = frames.length > 3 ? 2500 : 1800;
            let startTime = null;

            const backToTop = () => {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth"
                });
            };

            const animateScroll = (timestamp) => {
                if (!startTime) startTime = timestamp;

                const progress = timestamp - startTime;
                const percent = Math.min(progress / duration, 1);
                const ease = 1 - Math.pow(1 - percent, 3);

                window.scrollTo(0, start + (end - start) * ease);
                if (progress < duration) { requestAnimationFrame(animateScroll); }
                else { backToTop(); }
            };

            requestAnimationFrame(animateScroll);

        }, 250);

        return () => clearTimeout(timeout);
    }, [data, frames, animationMode]);

    useEffect(() => {
        if (!isPrinting) return;

        setTimeout(() => {
            const maps = document.querySelectorAll("[id^='map-report-component']");

            maps.forEach(el => {
                if (el.__google_map_instance) {
                    window.google.maps.event.trigger(
                        el.__google_map_instance,
                        "resize"
                    );
                }
            });
        }, 300);
    }, [isPrinting]);

    if (!corporation) return null;

    return (
        <>
            { corporation && (
                <div className="flex flex-col items-center">

                    <div className="flex justify-center">
                        <MapButtonsControl
                            variant="outside"
                            mapState={mapState}
                            controller={controller}
                        />
                    </div>

                    <div className="chart-divisor print:hidden !mt-3 w-full"></div>

                    <APIProvider apiKey={corporation?.maps_api_key ?? ""} language="pt-BR" libraries={["places"]}>
                        <div className={`w-full ${animationMode ? "flex flex-col items-stretch" : ""} ${animationMode ? "print:mt-[120px]" : "print:mt-[60px]"}`}>

                            { animationMode ? (() => {
                                const frame = frames[controller.currentFrame];
                                const frameData = controller.grouped[frame] || [];

                                return (
                                    <div className="w-full mx-auto flex flex-col items-center justify-center">
                                        <div className="text-center font-bold text-[17px] mb-1 bg-white shadow flex items-center justify-center">
                                            {controller.formatFrameLabel(frame)} (TOTAL: {frameData.length || 0})
                                        </div>

                                        <div style={{ width: isPrinting ? "95rem" : "100%", height: "500px" }}>
                                            <GoogleMaps
                                                id={'map-report-component'}
                                                ref={(map) => {
                                                    if (map && map.map) {
                                                        const div = map.map.getDiv();
                                                        div.__google_map_instance = map.map;
                                                    }
                                                }}
                                                style={{ width: "100%", height: "100%" }}
                                                defaultCenter={getPosition(frameData)}
                                                defaultZoom={10}
                                                gestureHandling={'greedy'}
                                                disableDefaultUI={true}
                                                zoomControl={false}
                                                streetViewControl={false}
                                                mapTypeControl={false}
                                                fullscreenControl={false}
                                            >
                                                <MapContent data={frameData} controller={controller} sync mapState={mapState} isPrinting={isPrinting}/>
                                            </GoogleMaps>
                                        </div>
                                    </div>
                                );
                            })() : (
                                <div className="flex flex-wrap justify-center gap-x-3 gap-y-3 print:gap-x-5 print:gap-y-10">
                                    {frames.map((frame, index) => {
                                        const frameData = controller.grouped[frame] || [];

                                        return (
                                            <div key={frame} className={`flex flex-col print:break-inside-avoid ${(index + 1) % 9 === 0 ? "print:break-after-page" : ""}`}>
                                                <div className="text-center font-bold text-[17px] mb-1 bg-white shadow flex items-center justify-center w-full">
                                                    {controller.formatFrameLabel(frame)} (TOTAL: {frameData.length || 0})
                                                </div>

                                                <div className={`print:!w-[470px]`} style={{ width: "600px", height: "300px" }}>
                                                    <GoogleMaps
                                                        id={`map-report-component-${frame}`}
                                                        ref={(map) => {
                                                            if (map && map.map) {
                                                                const div = map.map.getDiv();
                                                                div.__google_map_instance = map.map;
                                                            }
                                                        }}
                                                        style={{ width: "100%", height: "100%" }}
                                                        defaultCenter={getPosition(frameData)}
                                                        defaultZoom={10}
                                                        gestureHandling={'greedy'}
                                                        disableDefaultUI={true}
                                                        zoomControl={false}
                                                        streetViewControl={false}
                                                        mapTypeControl={false}
                                                        fullscreenControl={false}
                                                    >
                                                        <MapContent data={frameData} controller={controller} sync mapState={mapState} isPrinting={isPrinting}/>
                                                    </GoogleMaps>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                    </APIProvider>

                    {animationMode && (
                        <MapPlayerControl controller={controller}/>
                    )}
                </div>
            )}
        </>
    );
};

export default ReportContent;